import type { BusinessInformation } from "../types";
import { defaultFetchHeaders, defaultReadHeaders, fetchJson } from "../../utils";
import type { AdministrationAuditPage } from "./administrationOperations";

export type AdministrationMarketplaceNavigationTarget = {
  destination: string; filters: Record<string, string | string[]>;
  focusId: string; highlight: string;
};
export type AdministrationMarketplaceDashboardMetric = {
  key: string; label: string; value: number; previousValue: number | null;
  scope: "period" | "snapshot"; moneyTotals: Array<{ currency: string; totalCents: number }>;
  previousMoneyTotals: Array<{ currency: string; totalCents: number }>;
  components: Array<{ key: string; label: string; value: number; previousValue: number | null; target: AdministrationMarketplaceNavigationTarget }>;
};
export type AdministrationMarketplaceDashboard = {
  generatedAt: string;
  range: { label: string; start: string; end: string; previousStart: string; previousEnd: string };
  metrics: AdministrationMarketplaceDashboardMetric[];
  attention: { totalCount: number; items: Array<{ id: string; issue: string; area: string; impact: string; ageFrom: string; status: string; severity: string; entityId: string; target: AdministrationMarketplaceNavigationTarget }> };
  flow: Array<{ key: string; label: string; value: number; exceptions: number; target: AdministrationMarketplaceNavigationTarget }>;
  operationalHealth: {
    publication: { current: number; total: number; target: AdministrationMarketplaceNavigationTarget };
    discovery: { current: number; total: number; exceptions: number; target: AdministrationMarketplaceNavigationTarget };
    claims: { resolved: number; submitted: number; open: number; target: AdministrationMarketplaceNavigationTarget };
    oldestIssueAt: string | null;
    mix: Array<{ key: string; label: string; value: number; target: AdministrationMarketplaceNavigationTarget }>;
  };
  recentActivity: { items: AdministrationMarketplaceActivityEvent[]; totalCount: number };
};

export type AdministrationMarketplaceAllowanceOrganization = {
  organizationId: string; organizationName: string; ownerEmail: string;
  issuedPoints: number; remainingPoints: number; redemptions: number; adjustments: number;
};
export type AdministrationMarketplaceAllowanceOrganizationPage = {
  items: AdministrationMarketplaceAllowanceOrganization[]; nextCursor: string | null;
  totalCount: number; summary: Record<string, number>;
};
export type AdministrationMarketplaceAllowanceGrant = {
  id: string; source: string; originalPoints: number; remainingPoints: number;
  policyVersion: number; billingPeriod: string; issuedAt: string; expiresAt: string;
  subscriptionChargeId: string | null; metadata: Record<string, unknown>; policySnapshot: Record<string, unknown>;
  redemptions: Array<{ id: string; orderId: string; orderLineId: string; entitlementId: string | null; claimIds: string[]; pointsConsumed: number; allowanceValueCents: number; redeemedAt: string; restoredAt: string | null }>;
  adjustments: Array<{ id: string; remainingPointsDelta: number; previousOriginalPoints: number; resultingOriginalPoints: number; previousRemainingPoints: number; resultingRemainingPoints: number; previousExpiresAt: string; resultingExpiresAt: string; reason: string; actorId: string | null; createdAt: string }>;
};
export type AdministrationMarketplaceAllowanceLedger = { organizationId: string; organizationName: string; summary: Record<string, number>; grants: AdministrationMarketplaceAllowanceGrant[]; nextCursor: string | null };

export type AdministrationDiscoveryRunSummary = {
  id: string; status: string; retryOfId: string | null; observed: number;
  createdVenues: number; createdRecords: number; refreshedRecords: number;
  startedAt: string | null; finishedAt: string | null; createdAt: string;
};

export type AdministrationDiscoverySearch = {
  areaLabel?: string;
  programId?: string | null; programOwnerId?: string | null; programKind?: "sales" | "marketplace" | null;
  id: string; name: string; query: string; countryCode: string;
  latitude: string; longitude: string; radiusMeters: number;
  maxResultsPerSource: number; locale: string; sourceCodes: string[];
  enabled: boolean; intervalSeconds: number | null; nextRunAt: string | null;
  createdAt: string; salesProfileId: string | null; salesEnrichmentLimit?: number;
  recentRun: AdministrationDiscoveryRunSummary | null;
  coverageMode?: "fixed" | "explore"; coverageCellRadiusMeters?: number | null; coverageRevisitSeconds?: number;
  coverageProgress?: { areaIndex: number; areaCount: number; passNumber: number; latitude: number; longitude: number; radiusMeters: number } | null;
};

export type AdministrationDiscoveryRunSource = {
  sourceCode: string; status: string; observed: number; createdVenues: number;
  createdRecords: number; refreshedRecords: number; errorCode: string; errorMessage: string;
};

export type AdministrationDiscoveryRun = AdministrationDiscoveryRunSummary & {
  searchId: string; searchName: string; searchQuery: string; countryCode: string;
  requestSnapshot: Record<string, unknown>; sources: AdministrationDiscoveryRunSource[];
};

export type AdministrationDiscoverySummary = {
  totalSearches: number; enabledSearches: number; runningRuns: number;
  disabledSearches: number; pendingRuns: number; failedRuns: number; partialRuns: number;
  resultsObserved: number; venuesCreated: number; recordsCreated: number; recordsRefreshed: number;
};

export type AdministrationDiscoverySearchPage<Summary = AdministrationDiscoverySummary> = { items: AdministrationDiscoverySearch[]; nextCursor: string | null; totalCount: number; summary: Summary };
export type AdministrationDiscoveryRunPage = { items: AdministrationDiscoveryRun[]; nextCursor: string | null; totalCount: number; summary: AdministrationDiscoverySummary };

export type AdministrationDiscoveryOpportunityOutcome = {
  id: string; title: string; area: string; status: string; published: boolean;
  publishable: boolean; inventoryState: "available" | "reserved" | "acquired" | "unpublished";
  venueId: string; venueName: string; venueType: string; locality: string;
  administrativeArea: string; countryCode: string; latitude: string; longitude: string;
  sourceCodes: string[]; observedRunCount: number; lastObservedAt: string | null;
};

export type AdministrationDiscoveryOutcomePage = {
  items: AdministrationDiscoveryOpportunityOutcome[];
  totalCount: number;
};

export type AdministrationDiscoveryObservation = {
  id: string; runId: string; sourceCode: string; externalId: string;
  observedAt: string; action: "created" | "refreshed" | "stale";
  name: string; formattedAddress: string; locality: string;
  administrativeArea: string; countryCode: string; latitude: string | null;
  longitude: string | null; venueType: string; providerFieldsAvailable: boolean;
  retainUntil: string | null; canonicalVenueId: string | null;
  canonicalVenueName: string; resolutionStatus: "opportunity" | "no_opportunity" | "unresolved";
  opportunityCount: number;
  opportunities: { id: string; title: string }[];
};

export type AdministrationDiscoveryObservationSummary = {
  observed: number; created: number; refreshed: number; stale: number;
  uniqueProviderRecords: number; canonicalVenues: number;
  resolvedObservations: number; noOpportunityObservations: number;
  unresolvedObservations: number; opportunities: number;
  providerFieldsAvailable: number;
};

export type AdministrationDiscoveryObservationPage = {
  items: AdministrationDiscoveryObservation[]; nextCursor: string | null;
  totalCount: number; summary: AdministrationDiscoveryObservationSummary;
};

export type AdministrationDiscoverySource = {
  code: string; name: string; capabilities: string[]; configured: boolean; enabled: boolean;
  supportedCountries: string[]; allowDerivedStorage: boolean; allowFieldStorage: boolean;
  fieldRetentionDays: number | null; refreshSeconds: number | null;
  budget: AdministrationDiscoveryProviderBudget | null;
  maxRadiusMeters?: number; lastError?: string; lastErrorAt?: string | null; cooldownUntil?: string | null;
};

export type AdministrationDiscoveryProviderBudget = {
  sourceCode: string; dailyRequestLimit: number; dailyRequestsUsed: number;
  dailyRequestsRemaining: number; monthlyRequestLimit: number; monthlyRequestsUsed: number;
  monthlyRequestsRemaining: number; enforced: boolean; updatedAt: string;
};

export type AdministrationDiscoverySearchInput = {
  areaLabel?: string;
  name: string; query: string; countryCode: string; latitude: string; longitude: string;
  radiusMeters: number; maxResultsPerSource: number; locale: string;
  sourceCodes: string[]; enabled: boolean; intervalSeconds: number | null;
  salesProfileId?: string | null; salesEnrichmentLimit?: number;
  coverageMode?: "fixed" | "explore"; coverageCellRadiusMeters?: number | null; coverageRevisitSeconds?: number;
};

export type AdministrationMarketplaceInventoryPolicy = {
  id: string; name: string; query: string; categoryCode: string; leadTier: string;
  venueType: string; territoryMode: "map" | "administrative";
  countryCode: string; administrativeArea: string; locality: string;
  latitude: string; longitude: string; radiusMeters: number; minimumInventory: number;
  targetInventory: number; freshnessSeconds: number; cooldownSeconds: number;
  maxResultsPerSource: number; sourceCodes: string[]; enabled: boolean; updatedAt: string;
};
export type AdministrationMarketplaceInventoryPolicyTier = {
  id: string; name: string; leadTier: string; enabled: boolean;
};
export type AdministrationMarketplaceInventoryPolicyDetail = AdministrationMarketplaceInventoryPolicy & {
  tierPolicies: AdministrationMarketplaceInventoryPolicyTier[];
};
export type AdministrationMarketplaceInventoryPolicyInput = Omit<AdministrationMarketplaceInventoryPolicy, "id" | "updatedAt">;
export type AdministrationMarketplaceInventoryPolicyPage = { items: AdministrationMarketplaceInventoryPolicy[]; nextCursor: string | null; totalCount: number };

export type AdministrationMarketplaceDiscoveryDemand = {
  id: string; query: string; categoryCode: string; leadTier: string; countryCode: string;
  administrativeArea: string; locality: string; latitude: string; longitude: string;
  radiusMeters: number; desiredInventory: number; requestCount: number;
  triggerCounts: Record<string, number>; priority: number; priorityOverride: number | null;
  administrativelyHeld: boolean; status: string; holdReason: string;
  administrationReason: string;
  policyId: string | null; searchId: string | null; latestRunId: string | null; lastRequestedAt: string;
};

export type AdministrationMarketplaceDiscoveryDemandPage = {
  items: AdministrationMarketplaceDiscoveryDemand[]; nextCursor: string | null;
  totalCount: number; summary: Record<string, number>;
};
export type AdministrationMarketplaceDiscoveryDemandSignal = {
  id: string; trigger: string; organizationId: string | null; organizationName: string;
  desiredInventory: number; priority: number; occurredAt: string;
};
export type AdministrationMarketplaceDiscoveryDemandSignalPage = {
  items: AdministrationMarketplaceDiscoveryDemandSignal[]; nextCursor: string | null; totalCount: number;
};
export type AdministrationMarketplaceInventoryPolicyDemandPreview = {
  demandId: string; requestCount: number; holdReason: string;
  policy: AdministrationMarketplaceInventoryPolicyInput;
};
export type AdministrationMarketplaceInventoryPolicyDemandCreate = {
  policy: AdministrationMarketplaceInventoryPolicy;
  demand: AdministrationMarketplaceDiscoveryDemand;
};
export type AdministrationMarketplaceInventoryPolicyProposal = {
  key: string;
  reason: string;
  demand: AdministrationMarketplaceDiscoveryDemand;
  policy: AdministrationMarketplaceInventoryPolicyInput;
};
export type AdministrationMarketplaceInventoryPolicyProposalPage = {
  items: AdministrationMarketplaceInventoryPolicyProposal[];
  nextCursor: string | null;
  totalCount: number;
};

export type AdministrationMarketplacePricingRule = {
  id: string;
  version: number;
  currency: string;
  basePriceCents: number;
  subscriberPriceCents: number | null;
  configuration: Record<string, unknown>;
  enabled: boolean;
  activeFrom: string;
  activeUntil: string | null;
  createdAt: string;
};

export type AdministrationMarketplaceProduct = {
  photos?: { id: string; previewUrl: string; altText: string; isCover: boolean }[];
  description?: string;
  specifications?: string;
  id: string;
  sku: string;
  kind: string;
  name: string;
  leadTier: string;
  active: boolean;
  public: boolean;
  allowance: {
    participates: boolean;
    pointCost: number | null;
    standard: boolean;
    standardTier: string | null;
    policyVersion: number | null;
    policyActiveFrom: string | null;
    policyActiveUntil: string | null;
    coverageValueCents: number;
  };
  createdAt: string;
  pricingRules: AdministrationMarketplacePricingRule[];
};

export type AdministrationMarketplaceProductUpdate = {
  description?: string;
  specifications?: string;
  name: string;
  active: boolean;
  public: boolean;
};

export type AdministrationMarketplacePricingRuleCreate = {
  productId: string;
  currency: string;
  basePriceCents: number;
  subscriberPriceCents: number | null;
  configuration: Record<string, unknown>;
  enabled: boolean;
  activeFrom: string;
  activeUntil: string | null;
};

export type AdministrationMarketplacePricingRuleEnd = {
  activeUntil: string;
  reason: string;
};

export type MarketplacePricingSaleFunding = "cash" | "credit" | "allowance" | "refunded";
export type MarketplacePricingSaleStatus = "paid" | "fulfilled" | "refunded";

export type AdministrationMarketplacePricingVersionSalesFacet = {
  pricingRuleId: string;
  purchaseCount: number;
  cashPurchaseCount: number;
  creditPurchaseCount: number;
  allowancePurchaseCount: number;
  refundedPurchaseCount: number;
};

export type AdministrationMarketplacePricingSalesSummary = {
  currency: string;
  orderCount: number;
  purchaseCount: number;
  quantity: number;
  opportunityCount: number;
  organizationCount: number;
  grossRevenueCents: number;
  discountCents: number;
  creditCents: number;
  allowanceCents: number;
  cashRevenueCents: number;
  refundedCashCents: number;
  netCashRevenueCents: number;
  cashPurchaseCount: number;
  creditPurchaseCount: number;
  allowancePurchaseCount: number;
  refundedPurchaseCount: number;
  pricingVersionCount?: number | null;
  pricingVersions?: AdministrationMarketplacePricingVersionSalesFacet[] | null;
};

export type AdministrationMarketplacePricingSalesSummaryEnvelope = {
  pricingRule: AdministrationMarketplacePricingRule;
  summary: AdministrationMarketplacePricingSalesSummary;
};

export type AdministrationMarketplaceProductPricingSalesSummaryEnvelope = {
  productId: string;
  summary: AdministrationMarketplacePricingSalesSummary;
};

export type AdministrationMarketplacePricingSale = {
  id: string;
  orderId: string;
  organization: { id: string; name: string };
  purchaser: { id: string; name: string; email: string };
  opportunity: { id: string; title: string } | null;
  orderStatus: MarketplacePricingSaleStatus;
  quantity: number;
  subtotalCents: number;
  discountCents: number;
  creditCents: number;
  allowanceCents: number;
  cashCents: number;
  refundedCashCents: number;
  netCashCents: number;
  paidAt: string | null;
  fulfilledAt: string | null;
  createdAt: string;
  acquiredAt: string;
};

export type AdministrationMarketplacePricingSalesPage = {
  items: AdministrationMarketplacePricingSale[];
  nextCursor: string | null;
  totalCount: number;
};

export type AdministrationMarketplacePricingSalesQuery = {
  ruleId: string;
  query?: string;
  statuses?: MarketplacePricingSaleStatus[];
  funding?: MarketplacePricingSaleFunding[];
  dateFrom?: string;
  dateTo?: string;
  sort?: "newest" | "oldest";
  cursor?: string | null;
  limit?: number;
};

export type AdministrationMarketplaceAllowancePolicy = {
  id: string;
  version: number;
  enabled: boolean;
  activeFrom: string;
  activeUntil: string | null;
  grantPoints: number;
  displayTier: string;
  displayQuantity: number;
  standardProductId: string;
  tierPointCosts: Record<string, number>;
  pointValueCents: number;
  expirationMonths: number;
  changeReason: string;
  createdAt: string;
  createdById: string | null;
};

export type AdministrationMarketplaceAllowancePolicyCreate = Omit<
  AdministrationMarketplaceAllowancePolicy,
  "id" | "version" | "enabled" | "pointValueCents" | "createdAt" | "createdById"
>;
export type AdministrationMarketplaceAllowancePolicyDetail = AdministrationMarketplaceAllowancePolicy & {
  standardProductName: string; standardProductSku: string;
  createdByName: string; createdByEmail: string; usage: Record<string, number>;
  previousPolicy: { id: string; version: number; activeFrom: string; activeUntil: string | null } | null;
  previousUsage: Record<string, number> | null;
};
export type AdministrationMarketplaceAllowancePolicyRedemption = {
  id: string;
  grantId: string;
  organization: { id: string; name: string };
  orderId: string;
  orderLineId: string;
  opportunity: { id: string; title: string } | null;
  pointsConsumed: number;
  allowanceFundingCents: number;
  redeemedAt: string;
  restoredAt: string | null;
};
export type AdministrationMarketplaceAllowancePolicyRedemptionPage = {
  items: AdministrationMarketplaceAllowancePolicyRedemption[];
  nextCursor: string | null;
  totalCount: number;
};
export type AdministrationMarketplaceAllowancePolicyGrant = AdministrationMarketplaceAllowanceGrant & { organization: { id: string; name: string } };
export type AdministrationMarketplaceAllowancePolicyGrantPage = { items: AdministrationMarketplaceAllowancePolicyGrant[]; nextCursor: string | null; totalCount: number };
export type AdministrationMarketplaceAllowancePolicyActivityPage = { items: AdministrationMarketplaceActivityEvent[]; nextCursor: string | null; totalCount: number };

export type AdministrationMarketplaceOpportunitySummary = {
  total: number;
  available: number;
  published: number;
  unpublished: number;
  reserved: number;
  acquired: number;
  needsReview: number;
  acquiredRevenueTotals?: Array<{ currency: string; totalCents: number }>;
  acquiredGrossTotals?: Array<{ currency: string; totalCents: number }>;
  acquiredCreditTotals?: Array<{ currency: string; totalCents: number }>;
  acquiredAllowanceTotals?: Array<{ currency: string; totalCents: number }>;
  totalPublicPotentialTotals?: Array<{ currency: string; totalCents: number }>;
  totalSubscriberPotentialTotals?: Array<{ currency: string; totalCents: number }>;
  availablePublicPotentialTotals?: Array<{ currency: string; totalCents: number }>;
  availableSubscriberPotentialTotals?: Array<{ currency: string; totalCents: number }>;
  reservedPublicPotentialTotals?: Array<{ currency: string; totalCents: number }>;
  reservedSubscriberPotentialTotals?: Array<{ currency: string; totalCents: number }>;
  needsReviewPublicPotentialTotals?: Array<{ currency: string; totalCents: number }>;
  needsReviewSubscriberPotentialTotals?: Array<{ currency: string; totalCents: number }>;
};

export type AdministrationMarketplaceOpportunity = {
  matchReasons?: { label: string; value: string }[];
  id: string;
  title: string;
  area: string;
  description: string;
  administrationNotes?: string;
  status: string;
  published: boolean;
  publishable: boolean;
  inventoryState: "available" | "reserved" | "acquired" | "unpublished";
  reservationExpiresAt: string | null;
  financial?: {
    organization: { id: string; name: string };
    currency: string;
    orderStatus: string;
    grossCents: number;
    discountCents: number;
    creditCents: number;
    allowanceCents: number;
    cashCents: number;
    refundedCashCents: number;
    netCashCents: number;
    acquiredAt: string;
  } | null;
  blockers: string[];
  reviewBlockers?: Array<{
    code: string;
    message: string;
    resolution: "verify" | "edit_area" | "edit_title" | "reassess" | "review_qualification" | "view_canonical_venue" | "view_venue" | "view_purchase" | "view_reservation";
    resolvable: boolean;
  }>;
  tier: string;
  tierOverride?: string;
  tierReviewReason?: string;
  appointmentReady: boolean;
  appointmentReconfirmationRequired?: boolean;
  categoryIds: string[];
  sourceCodes?: string[];
  venueName: string;
  venueType: string;
  formattedAddress: string;
  locality: string;
  administrativeArea: string;
  countryCode: string;
  complexVenue: boolean;
  lastVerifiedAt: string | null;
  verifiedBy?: { id: string; name: string; email: string } | null;
  assessments: Array<{
    id: string;
    categoryId: string;
    categoryCode: string;
    categoryName: string;
    ruleVersion: string;
    evidenceExpiresAt?: string | null;
    tier: string;
    suitabilityScore: number;
    confidenceScore: number;
    salesReadinessScore: number;
    signals: Array<Record<string, unknown>>;
  }>;
  createdAt: string;
  updatedAt: string;
};

export type AdministrationMarketplaceOpportunityPage = {
  items: AdministrationMarketplaceOpportunity[];
  nextCursor: string | null;
  totalCount: number;
  financialSummary: Array<{
    currency: string; grossCents: number; discountCents: number;
    creditCents: number; allowanceCents: number; cashCents: number;
    fundingCents: number; refundedCashCents: number; netCashCents: number;
  }>;
  summary: AdministrationMarketplaceOpportunitySummary;
  scopeSummary: AdministrationMarketplaceOpportunitySummary;
  nextChangeAt: string | null;
};

export type AdministrationMarketplaceOpportunityDescriptionUpdate = {
  description: string;
};

export type AdministrationMarketplaceLeadClaim = {
  refundStatus?: string; refundRequestedCents?: number; refundFailureReason?: string;
  withdrawnAt?: string | null;
  withdrawnBy?: { id: string; name: string } | null;
  updates?: Array<{ id: string; note: string; authorName: string; authorEmail?: string; createdAt: string; photos: { id: string; url: string }[] }>;
  id: string; entitlementId: string; organizationId: string; reason: string;
  description: string; evidence: string[]; photos?: { id: string; url: string }[]; status: string; remedy: string;
  reviewerNote: string; lateSubmissionAccepted: boolean; abuseFlagged: boolean;
  replacementEntitlementId: string | null; marketplaceCreditGrantId: string | null;
  refundedCents: number; stripeRefundId: string;
  reviewedBy: { id: string; name: string; email: string } | null;
  reviewedAt: string | null; createdAt: string;
  correctedInformation: Record<string, unknown>;
  organizationName: string; purchaserName: string;
  opportunityId: string | null; opportunityTitle: string; opportunityTier: string;
};

export type AdministrationMarketplaceLeadClaimSummary = {
  total: number; submitted: number; inReview: number; approved: number;
  rejected: number; withdrawn?: number; riskReview: number;
};

export type AdministrationMarketplaceLeadClaimQuery = {
  query?: string; statuses?: string[]; reason?: string; remedy?: string | null;
  submittedAfter?: string; submittedBefore?: string; abuseFlagged?: boolean;
  reviewedAfter?: string; reviewedBefore?: string;
  sort?: "newest" | "oldest"; cursor?: string | null; limit?: number;
};

export type AdministrationMarketplaceLeadClaimPage = {
  items: AdministrationMarketplaceLeadClaim[];
  nextCursor: string | null;
  totalCount: number;
  summary: AdministrationMarketplaceLeadClaimSummary;
  scopeSummary: AdministrationMarketplaceLeadClaimSummary;
};

export type AdministrationMarketplaceLeadClaimReview = {
  claim: AdministrationMarketplaceLeadClaim;
  allowedRemedies: string[];
  remedyAvailability: Array<{ remedy: string; label: string; available: boolean; unavailableReason: string }>;
  organization: { id: string; name: string };
  purchaser: { id: string; name: string; email: string };
  entitlement: { id: string; status: string; acquiredAt: string };
  order: Record<string, unknown>;
  opportunity: { currentContacts?: Array<{ id: string; name: string; role: string; email: string; phone: string }>; correctionFields?: Array<{ key: string; label: string; value: string; scope: "opportunity" | "venue"; group: "opportunity" | "venue" | "address"; options?: Array<{ value: string; label: string }> }>; id: string; title: string; tier: string; area: string; venueName: string; formattedAddress: string; locality: string; administrativeArea: string; countryCode: string; categoryCodes: string[]; lastVerifiedAt: string | null; originalContacts: Array<{ name: string; role: string; email: string; phone: string }>; contactSnapshotSource: "acquisition" | "current" };
  risk: { abuseFlagged: boolean; recentClaimCount: number };
  resolution: {
    replacement: null | {
      entitlementId: string; entitlementStatus: string; grantedAt: string;
      opportunityId: string; title: string; tier: string; venueName: string;
      area: string; locality: string; administrativeArea: string; countryCode: string;
      costCents: number;
    };
    marketplaceCredit: null | {
      grantId: string; amountCents: number; remainingCents: number;
      currency: string; issuedAt: string;
    };
    allowanceRestoration: null | {
      pointsRestored: number; valueCents: number; restoredAt: string; grants?: Array<{ points: number; expiresAt: string }>;
    };
  };
};

export type AdministrationMarketplaceReplacementCandidate = {
  opportunityId: string;
  distanceMeters?: number | null;
  title: string;
  area: string;
  venueName: string;
  locality: string;
  administrativeArea: string;
  countryCode: string;
  categoryCodes: string[];
  tier: string;
  lastVerifiedAt: string | null;
  currency: string;
  publicPriceCents: number | null;
  subscriberPriceCents: number | null;
  replacementPriceCents: number;
};

export type AdministrationMarketplaceReplacementCandidatePage = {
  items: AdministrationMarketplaceReplacementCandidate[];
  nextCursor: string | null;
};

export type AdministrationMarketplaceContact = { id: string; name: string; role: string; email: string; phone: string };

export type AdministrationMarketplaceOpportunityDetail = {
  businessInformation?: BusinessInformation;
  appointmentQualification?: (Omit<MarketplaceAppointmentConfirmation, "channel" | "requestedAction"> & { channel: string; requestedAction: string; expiresAt: string | null; retiredAt: string | null; withdrawnAt?: string | null; withdrawalReason?: string; verifiedById: string | null }) | null;
  appointmentConfirmationValidDays?: number;
  buyerAdditions?: { businessInformation?: BusinessInformation; suppliedBusinessInformation?: BusinessInformation | null; businessInformationEdited?: boolean; organizationName?: string; contacts: Array<AdministrationMarketplaceContact & { addedBy: string; createdAt: string; updatedAt: string }>; photos: Array<{ id: string; originalUrl: string; addedBy: string; createdAt: string }> };
  contacts: AdministrationMarketplaceContact[];
  opportunity: AdministrationMarketplaceOpportunity;
  venue: {
    id: string; name: string; type: string; status: string; formattedAddress: string;
    addressLines: string[]; locality: string; administrativeArea: string; postalCode: string;
    countryCode: string; timezone: string; latitude: string; longitude: string;
    complex: boolean; lastVerifiedAt: string | null;
    providerBusinessDetails?: Array<{ sourceCode: string; phone: string; website: string; observedAt: string }>;
  };
  publicApproximateArea: { centerLatitude: number; centerLongitude: number; radiusMiles: number };
  product: {
    id: string; sku: string; name: string; tier: string; active: boolean; public: boolean;
    allowance?: AdministrationMarketplaceProduct["allowance"];
  } | null;
  currentPricing: { id: string; version: number; currency: string; basePriceCents: number; subscriberPriceCents: number | null; activeFrom: string; activeUntil: string | null } | null;
  commerce: {
    state: "available" | "reserved" | "acquired" | "unpublished";
    reservationStartedAt?: string; reservationExpiresAt?: string; orderLineId?: string; orderId?: string; orderStatus?: string;
    organization?: { id: string; name: string; timezone?: string; accountMode?: string; createdAt?: string; owner?: { id: string; name: string; email: string; phone: string } }; purchaser?: { id: string; name: string; email: string; phone?: string };
    currency?: string; product?: { id: string; name: string; sku: string }; pricingRuleId?: string | null;
    pricingSnapshot?: Record<string, unknown>; grossCents?: number; discountCents?: number;
    marketplaceDescriptionAtAcquisition?: string | null;
    creditCents?: number; allowanceCents?: number; cashCents?: number; refundedCashCents?: number;
    netCashCents?: number; paidAt?: string | null; fulfilledAt?: string | null;
    entitlementId?: string; entitlementStatus?: string; acquiredAt?: string;
  };
  claims: { count: number; openCount: number; items: Array<{ id: string; status: string; reason: string; description: string; remedy: string; refundStatus?: string; refundedCents: number; submittedAt: string; reviewedAt: string | null; submittedBy: string; reviewedBy: string | null }> };
  assessments: Array<AdministrationMarketplaceOpportunity["assessments"][number] & { eligible: boolean; current: boolean; explanation: string; createdAt: string }>;
  activityPreview: AdministrationMarketplaceActivityEvent[];
  capabilities: { canPublish: boolean; canWithdraw: boolean; canEditDescription: boolean; canEditContacts: boolean; publishBlockedReasons: string[]; withdrawBlockedReason: string | null };
  nextChangeAt: string | null;
};

export type AdministrationMarketplaceOpportunityMedia = {
  id: string;
  originalUrl: string;
  previewUrl: string;
  altText: string;
  representation: "actual" | "representative";
  sortOrder: number;
  isCover: boolean;
  approved: boolean;
  suppressed: boolean;
  createdAt: string;
};

export type AdministrationMarketplaceOpportunityGalleryItem = Pick<
  AdministrationMarketplaceOpportunityMedia,
  "id" | "representation" | "isCover" | "approved" | "suppressed"
> & { altText?: string };

export type AdministrationMarketplaceOpportunityQuery = {
  productId?: string;
  policyId?: string;
  tier?: "appointment_ready" | "premium" | "qualified" | "researched";
  query?: string;
  publication?: "all" | "published" | "unpublished" | Array<"published" | "unpublished">;
  publishability?: "all" | "publishable" | "blocked" | Array<"publishable" | "blocked">;
  inventoryState?: "all" | "available" | "reserved" | "acquired" | Array<"available" | "reserved" | "acquired">;
  sort?: "newest" | "oldest" | "tier_ascending" | "tier_descending";
  cursor?: string | null;
  limit?: number;
  view?: "operational" | "financial";
  dateFrom?: string;
  dateTo?: string;
};

export type MarketplaceActivityGroup = "configuration" | "inventory" | "acquisition" | "claim";
export type MarketplaceActivitySource = "administrator" | "customer" | "system";
export type MarketplaceActivitySeverity = "info" | "warning" | "error" | "critical";

export type AdministrationMarketplaceActivityEvent = {
  id: string;
  group: MarketplaceActivityGroup;
  action: string;
  source: MarketplaceActivitySource;
  severity: MarketplaceActivitySeverity;
  summary: string;
  relatedType: string;
  relatedId: string;
  relatedLabel: string;
  details: Record<string, unknown>;
  occurredAt: string;
  actor: { id: string; name: string; email: string } | null;
};

export type AdministrationMarketplaceActivitySummary = {
  total: number;
  comparisonPercent: number | null;
  comparisonPeriod: {
    currentStart: string;
    currentEnd: string;
    previousStart: string;
    previousEnd: string;
    previousTotal: number;
  } | null;
  configuration: number;
  productChanges: number;
  pricingChanges: number;
  inventory: number;
  published: number;
  withdrawn: number;
  checkoutChanges: number;
  acquired: number;
  acquiredTotals: Array<{ currency: string; totalCents: number }>;
  claims: number;
  claimsSubmitted: number;
  claimsResolved: number;
};

export type AdministrationMarketplaceActivityPage = {
  items: AdministrationMarketplaceActivityEvent[];
  nextCursor: string | null;
  totalCount: number;
  summary: AdministrationMarketplaceActivitySummary;
};

export type AdministrationMarketplaceActivityQuery = {
  productId?: string;
  opportunityId?: string;
  query?: string;
  groups?: MarketplaceActivityGroup[];
  sources?: MarketplaceActivitySource[];
  severities?: MarketplaceActivitySeverity[];
  dateFrom?: string;
  dateTo?: string;
  sort?: "newest" | "oldest";
  cursor?: string | null;
  limit?: number;
};

const backend = process.env.REACT_APP_BACKEND_URL || "";

const administrationMarketplaceMediaUrl = (value: string) => {
  if (!value || /^(?:https?:|data:|blob:)/i.test(value) || !backend) return value;
  try {
    return new URL(value, `${backend.replace(/\/+$/, "")}/`).toString();
  } catch {
    return value;
  }
};

const normalizeAdministrationMarketplaceMedia = (
  media: AdministrationMarketplaceOpportunityMedia,
): AdministrationMarketplaceOpportunityMedia => ({
  ...media,
  originalUrl: administrationMarketplaceMediaUrl(media.originalUrl),
  previewUrl: administrationMarketplaceMediaUrl(media.previewUrl),
});

async function get<T>(path: string, errorMessage: string, signal?: AbortSignal): Promise<T> {
  return fetchJson(
    `${backend}${path}`,
    {
      signal,
      method: "GET",
      credentials: "include",
      headers: defaultReadHeaders(),
    },
    errorMessage,
  );
}

async function mutate<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT",
  body: unknown,
  errorMessage: string,
): Promise<T> {
  return fetchJson(
    `${backend}${path}`,
    {
      method,
      credentials: "include",
      headers: await defaultFetchHeaders(),
      body: JSON.stringify(body),
    },
    errorMessage,
  );
}

export type AdministrationMarketplaceOverviewQuery = { dateFrom?: string; dateTo?: string; attentionSort?: "priority" | "oldest" | "newest" };

export const fetchAdministrationMarketplaceOverview = (query: AdministrationMarketplaceOverviewQuery = {}) => {
  const params = new URLSearchParams();
  if (query.dateFrom) params.set("date_from", query.dateFrom);
  if (query.dateTo) params.set("date_to", query.dateTo);
  if (query.attentionSort) params.set("attention_sort", query.attentionSort);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return get<AdministrationMarketplaceDashboard>(
    `/administration/marketplace/overview${suffix}`,
    "Failed to load Marketplace overview",
  );
};

export const fetchAdministrationMarketplaceAllowanceOrganizations = (query: { q?: string; status?: string; cursor?: string | null; limit?: number } = {}, signal?: AbortSignal) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50) });
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.status) params.set("status", query.status);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceAllowanceOrganizationPage>(`/administration/marketplace/lead-allowances?${params}`, "Failed to load Marketplace allowances", signal);
};
export const fetchAdministrationMarketplaceAllowanceLedger = (organizationId: string, query: { cursor?: string | null; limit?: number } = {}, signal?: AbortSignal) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50) });
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceAllowanceLedger>(`/administration/organizations/${encodeURIComponent(organizationId)}/marketplace/lead-allowances?${params}`, "Failed to load Marketplace allowance ledger", signal);
};
export const adjustAdministrationMarketplaceAllowanceGrant = (grantId: string, adjustment: { remainingPointsDelta: number; expiresAt: string | null; reason: string }) => mutate<{ id: string }>(`/administration/marketplace/lead-allowances/${encodeURIComponent(grantId)}`, "PATCH", adjustment, "Failed to adjust Marketplace allowance grant");

export type DiscoverySearchScope = { type: "sales"; profileId?: string }
  | { type: "program"; kind: DiscoveryProgramKind; ownerId: string };

type DiscoverySearchQuery = { q?: string; enabled?: boolean; latestStatus?: string; source?: string; sort?: "newest" | "oldest"; cursor?: string | null; limit?: number };
export function fetchAdministrationDiscoverySearches(query?: DiscoverySearchQuery, signal?: AbortSignal): Promise<AdministrationDiscoverySearchPage>;
export function fetchAdministrationDiscoverySearches(query: DiscoverySearchQuery & { scope: DiscoverySearchScope }, signal?: AbortSignal): Promise<AdministrationDiscoverySearchPage<Record<string, number>>>;
export function fetchAdministrationDiscoverySearches(query: DiscoverySearchQuery & { scope?: DiscoverySearchScope } = {}, signal?: AbortSignal) {
  const scope = query.scope;
  const endpoint = scope?.type === "program" ? `${discoveryProgramPath(scope.kind, scope.ownerId)}/searches`
    : scope?.type === "sales" ? "/administration/sales/discovery/searches" : "/administration/prospecting/searches";
  const params = new URLSearchParams({ limit: String(query.limit || 50), sort: query.sort || "newest" });
  if (query.q?.trim()) params.set(scope ? "query" : "q", query.q.trim());
  if (scope?.type === "sales" && scope.profileId) params.set("profile_id", scope.profileId);
  if (query.enabled !== undefined) params.set("enabled", String(query.enabled));
  if (query.latestStatus) params.set("latest_status", query.latestStatus);
  if (query.source) params.set("source", query.source);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationDiscoverySearchPage<Record<string, number>>>(`${endpoint}?${params}`, "Failed to load Discovery searches", signal);
}

export const fetchAdministrationDiscoverySearch = (searchId: string) =>
  get<AdministrationDiscoverySearch>(`/administration/prospecting/searches/${encodeURIComponent(searchId)}`, "Failed to load Discovery search");

export const fetchAdministrationDiscoveryRun = (runId: string) =>
  get<AdministrationDiscoveryRun>(`/administration/prospecting/runs/${encodeURIComponent(runId)}`, "Failed to load Discovery run");

export const fetchAdministrationDiscoverySearchOutcomes = (searchId: string) =>
  get<AdministrationDiscoveryOutcomePage>(`/administration/prospecting/searches/${encodeURIComponent(searchId)}/outcomes`, "Failed to load Discovery Search outcomes");

export const fetchAdministrationDiscoveryRunOutcomes = (runId: string) =>
  get<AdministrationDiscoveryOutcomePage>(`/administration/prospecting/runs/${encodeURIComponent(runId)}/outcomes`, "Failed to load Discovery Run outcomes");

export const fetchAdministrationDiscoveryObservations = (query: { runId?: string; searchId?: string; source?: string; cursor?: string | null; limit?: number; sort?: "newest" | "oldest" } = {}) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50), sort: query.sort || "newest" });
  if (query.runId) params.set("run_id", query.runId);
  if (query.searchId) params.set("search_id", query.searchId);
  if (query.source) params.set("source", query.source);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationDiscoveryObservationPage>(`/administration/prospecting/observations?${params}`, "Failed to load Discovery observations");
};

export const fetchAdministrationDiscoverySources = () =>
  get<AdministrationDiscoverySource[]>("/administration/prospecting/sources", "Failed to load Discovery providers");

export const updateAdministrationDiscoverySearch = (searchId: string, input: AdministrationDiscoverySearchInput) =>
  mutate<AdministrationDiscoverySearch>(`/administration/prospecting/searches/${encodeURIComponent(searchId)}`, "PUT", input, "Failed to update Discovery search");

export const createAdministrationDiscoverySearch = (input: AdministrationDiscoverySearchInput) =>
  mutate<AdministrationDiscoverySearch>("/administration/prospecting/searches", "POST", input, "Failed to create Discovery search");

async function discoveryAction(path: string, idempotencyKey: string, errorMessage: string) {
  return fetchJson(
    `${backend}${path}`,
    { method: "POST", credentials: "include", headers: { ...await defaultFetchHeaders(), "Idempotency-Key": idempotencyKey }, body: JSON.stringify({}) },
    errorMessage,
  ) as Promise<AdministrationDiscoveryRun>;
}

export const startAdministrationDiscoveryRun = (searchId: string, idempotencyKey: string) =>
  discoveryAction(`/administration/prospecting/searches/${encodeURIComponent(searchId)}/runs`, idempotencyKey, "Failed to start Discovery run");

export const retryAdministrationDiscoveryRun = (runId: string, idempotencyKey: string) =>
  discoveryAction(`/administration/prospecting/runs/${encodeURIComponent(runId)}/retry`, idempotencyKey, "Failed to retry Discovery run");

export const fetchAdministrationMarketplaceInventoryPolicies = (query: { status?: "understocked"; cursor?: string | null; limit?: number } = {}) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50) });
  if (query.status) params.set("status", query.status);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceInventoryPolicyPage>(`/administration/marketplace/inventory-policies?${params}`, "Failed to load inventory policies");
};

export type AdministrationMarketplaceInventoryPolicyProposalSort = "tier" | "inventory" | "requests" | "requested";

export const fetchAdministrationMarketplaceInventoryPolicyProposals = (query: {
  q?: string;
  sort?: AdministrationMarketplaceInventoryPolicyProposalSort;
  direction?: "ascending" | "descending";
  cursor?: string | null;
  limit?: number;
} = {}) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50) });
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.sort) params.set("sort", query.sort);
  if (query.direction) params.set("direction", query.direction);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceInventoryPolicyProposalPage>(`/administration/marketplace/inventory-policy-proposals?${params}`, "Failed to load policy review queue");
};

export const createAdministrationMarketplaceInventoryPolicy = (input: AdministrationMarketplaceInventoryPolicyInput) =>
  mutate<AdministrationMarketplaceInventoryPolicy>("/administration/marketplace/inventory-policies", "POST", input, "Failed to create inventory policy");

export const fetchAdministrationMarketplaceInventoryPolicy = (policyId: string) =>
  get<AdministrationMarketplaceInventoryPolicyDetail>(`/administration/marketplace/inventory-policies/${encodeURIComponent(policyId)}`, "Failed to load inventory policy");

export const updateAdministrationMarketplaceInventoryPolicy = (policyId: string, input: AdministrationMarketplaceInventoryPolicyInput) =>
  mutate<AdministrationMarketplaceInventoryPolicyDetail>(`/administration/marketplace/inventory-policies/${encodeURIComponent(policyId)}`, "PUT", input, "Failed to update inventory policy");

export const createAdministrationMarketplaceMissingTierPolicies = (policyId: string, leadTiers: string[]) =>
  mutate<{ items: AdministrationMarketplaceInventoryPolicy[] }>(`/administration/marketplace/inventory-policies/${encodeURIComponent(policyId)}/missing-tiers`, "POST", { leadTiers }, "Failed to create missing tier policies");

export const fetchAdministrationMarketplaceDiscoveryDemands = (query: { q?: string; status?: string; cursor?: string | null; limit?: number } = {}) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50) });
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.status) params.set("status", query.status);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceDiscoveryDemandPage>(`/administration/marketplace/discovery-demands?${params}`, "Failed to load Discovery demand");
};

export const fetchAdministrationMarketplaceDiscoveryDemand = (demandId: string) =>
  get<AdministrationMarketplaceDiscoveryDemand>(`/administration/marketplace/discovery-demands/${encodeURIComponent(demandId)}`, "Failed to load Discovery demand");

export const fetchAdministrationMarketplaceInventoryPolicyDemandPreview = (demandId: string) =>
  get<AdministrationMarketplaceInventoryPolicyDemandPreview>(`/administration/marketplace/discovery-demands/${encodeURIComponent(demandId)}/inventory-policy-preview`, "Failed to preview inventory policy");

export const createAdministrationMarketplaceInventoryPolicyForDemand = (demandId: string, input: AdministrationMarketplaceInventoryPolicyInput) =>
  mutate<AdministrationMarketplaceInventoryPolicyDemandCreate>(`/administration/marketplace/discovery-demands/${encodeURIComponent(demandId)}/inventory-policy`, "POST", input, "Failed to create inventory policy and re-evaluate demand");

export const controlAdministrationMarketplaceDiscoveryDemand = (demandId: string, input: { held?: boolean; priority?: number; clearPriority?: boolean; reason: string }) =>
  mutate<AdministrationMarketplaceDiscoveryDemand>(`/administration/marketplace/discovery-demands/${encodeURIComponent(demandId)}`, "PATCH", input, "Failed to update Discovery demand");

export const configureAdministrationDiscoverySource = (code: string, input: { enabled: boolean; supportedCountries: string[]; allowDerivedStorage: boolean; allowFieldStorage: boolean; fieldRetentionDays: number | null; refreshSeconds: number | null }) =>
  mutate<AdministrationDiscoverySource>(`/administration/prospecting/sources/${encodeURIComponent(code)}`, "PUT", input, "Failed to update Discovery provider");

export const configureAdministrationDiscoveryProviderBudget = (code: string, input: { dailyRequestLimit: number; monthlyRequestLimit: number; enforced: boolean }) =>
  mutate<AdministrationDiscoveryProviderBudget>(`/administration/prospecting/sources/${encodeURIComponent(code)}/budget`, "PUT", input, "Failed to update Discovery provider budget");

export const fetchAdministrationDiscoveryRuns = (query: { searchId?: string; q?: string; status?: string; source?: string; dateFrom?: string; dateTo?: string; sort?: "newest" | "oldest"; cursor?: string | null; limit?: number } = {}) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50), sort: query.sort || "newest" });
  if (query.searchId) params.set("search_id", query.searchId);
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.status) params.set("status", query.status);
  if (query.source) params.set("source", query.source);
  if (query.dateFrom) params.set("date_from", query.dateFrom);
  if (query.dateTo) params.set("date_to", query.dateTo);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationDiscoveryRunPage>(`/administration/prospecting/runs?${params}`, "Failed to load Discovery runs");
};

export const fetchAdministrationMarketplaceProducts = () =>
  get<AdministrationMarketplaceProduct[]>(
    "/administration/marketplace/products",
    "Failed to load Marketplace products",
  );

export const updateAdministrationMarketplaceProduct = (
  productId: string,
  product: AdministrationMarketplaceProductUpdate,
) => mutate<AdministrationMarketplaceProduct>(
  `/administration/marketplace/products/${encodeURIComponent(productId)}`,
  "PATCH",
  product,
  "Failed to update Marketplace product",
);

export const createAdministrationMarketplacePricingRule = (
  rule: AdministrationMarketplacePricingRuleCreate,
) => mutate<AdministrationMarketplacePricingRule>(
  "/administration/marketplace/pricing-rules",
  "POST",
  rule,
  "Failed to create Marketplace pricing version",
);

export const endAdministrationMarketplacePricingRule = (
  ruleId: string,
  change: AdministrationMarketplacePricingRuleEnd,
) => mutate<AdministrationMarketplacePricingRule>(
  `/administration/marketplace/pricing-rules/${encodeURIComponent(ruleId)}/end`,
  "POST",
  change,
  "Failed to end Marketplace pricing version",
);

export const fetchAdministrationMarketplacePricingSalesSummary = (ruleId: string) =>
  get<AdministrationMarketplacePricingSalesSummaryEnvelope>(
    `/administration/marketplace/pricing-rules/${encodeURIComponent(ruleId)}/sales-summary`,
    "Failed to load Marketplace pricing sales summary",
  );

export const fetchAdministrationMarketplaceProductPricingSalesSummary = (productId: string) =>
  get<AdministrationMarketplaceProductPricingSalesSummaryEnvelope>(
    `/administration/marketplace/products/${encodeURIComponent(productId)}/sales-summary`,
    "Failed to load Marketplace product pricing sales summary",
  );

export const fetchAdministrationMarketplacePricingSales = (
  query: AdministrationMarketplacePricingSalesQuery,
) => {
  const params = new URLSearchParams({
    limit: String(query.limit || 50),
    sort: query.sort || "newest",
  });
  if (query.query?.trim()) params.set("q", query.query.trim());
  if (query.statuses?.length) params.set("statuses", [...query.statuses].sort().join(","));
  if (query.funding?.length) params.set("funding", [...query.funding].sort().join(","));
  if (query.dateFrom) params.set("date_from", query.dateFrom);
  if (query.dateTo) params.set("date_to", query.dateTo);
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplacePricingSalesPage>(
    `/administration/marketplace/pricing-rules/${encodeURIComponent(query.ruleId)}/order-lines?${params.toString()}`,
    "Failed to load Marketplace pricing sales",
  );
};

export const fetchAdministrationMarketplaceAllowancePolicies = () =>
  get<AdministrationMarketplaceAllowancePolicy[]>(
    "/administration/marketplace/allowance-policies",
    "Failed to load Marketplace allowance policies",
  );
export const fetchAdministrationMarketplaceAllowancePolicy = (policyId: string) =>
  get<AdministrationMarketplaceAllowancePolicyDetail>(`/administration/marketplace/allowance-policies/${encodeURIComponent(policyId)}`, "Failed to load Marketplace allowance policy");
export const fetchAdministrationMarketplaceAllowancePolicyRedemptions = (
  policyId: string,
  query: { q?: string; sort?: "newest" | "oldest"; cursor?: string | null; limit?: number } = {},
) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50), sort: query.sort || "newest" });
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceAllowancePolicyRedemptionPage>(`/administration/marketplace/allowance-policies/${encodeURIComponent(policyId)}/redemptions?${params}`, "Failed to load allowance policy usage");
};
export const fetchAdministrationMarketplaceAllowancePolicyGrants = (
  policyId: string, query: { q?: string; sort?: "newest" | "oldest"; cursor?: string | null; limit?: number } = {},
) => {
  const params = new URLSearchParams({ limit: String(query.limit || 50), sort: query.sort || "newest" });
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceAllowancePolicyGrantPage>(`/administration/marketplace/allowance-policies/${encodeURIComponent(policyId)}/grants?${params}`, "Failed to load allowance policy grants");
};
export const fetchAdministrationMarketplaceAllowancePolicyActivity = (
  policyId: string, query: { q?: string; sort?: "newest" | "oldest"; limit?: number } = {},
) => {
  const params = new URLSearchParams({ limit: String(query.limit || 100), sort: query.sort || "newest" });
  if (query.q?.trim()) params.set("q", query.q.trim());
  return get<AdministrationMarketplaceAllowancePolicyActivityPage>(`/administration/marketplace/allowance-policies/${encodeURIComponent(policyId)}/activity?${params}`, "Failed to load allowance policy activity");
};

export const createAdministrationMarketplaceAllowancePolicy = (
  policy: AdministrationMarketplaceAllowancePolicyCreate,
) => mutate<AdministrationMarketplaceAllowancePolicy>(
  "/administration/marketplace/allowance-policies",
  "POST",
  policy,
  "Failed to create Marketplace allowance policy",
);

export const fetchAdministrationMarketplaceOpportunities = (
  query: AdministrationMarketplaceOpportunityQuery,
) => {
  const filterParam = (value: string | string[] | undefined) =>
    Array.isArray(value) ? (value.length ? [...value].sort().join(",") : "all") : value || "all";
  const params = new URLSearchParams({
    publication: filterParam(query.publication),
    publishability: filterParam(query.publishability),
    inventory_state: filterParam(query.inventoryState),
    sort: query.sort || "newest",
    limit: String(query.limit || 50),
  });
  if (query.productId) params.set("product_id", query.productId);
  if (query.policyId) params.set("policy_id", query.policyId);
  if (query.tier) params.set("tier", query.tier);
  if (query.query?.trim()) params.set("query", query.query.trim());
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.view) params.set("view", query.view);
  if (query.dateFrom) params.set("date_from", query.dateFrom);
  if (query.dateTo) params.set("date_to", query.dateTo);
  return get<AdministrationMarketplaceOpportunityPage>(
    `/administration/marketplace/opportunities?${params.toString()}`,
    "Failed to load Marketplace opportunity inventory",
  );
};

export const fetchAdministrationMarketplaceOpportunity = (opportunityId: string) =>
  get<AdministrationMarketplaceOpportunityDetail>(
    `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}`,
    "Failed to load Marketplace opportunity",
  );

export const updateAdministrationMarketplaceOpportunityContacts = (
  opportunityId: string,
  change: { expectedUpdatedAt: string; contacts: Array<Omit<AdministrationMarketplaceContact, "id"> & { id: string | null }> },
) => mutate<AdministrationMarketplaceOpportunityDetail>(
  `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/contacts`,
  "PATCH", change, "Failed to update opportunity contacts",
);

export const fetchAdministrationMarketplaceOpportunityMedia = (opportunityId: string, kind: "opportunities" | "products" = "opportunities") =>
  get<AdministrationMarketplaceOpportunityMedia[]>(
    `/administration/marketplace/${kind}/${encodeURIComponent(opportunityId)}/media`,
    "Failed to load Marketplace opportunity media",
  ).then((items) => items.map(normalizeAdministrationMarketplaceMedia));

export async function uploadAdministrationMarketplaceOpportunityMedia(
  opportunityId: string,
  file: File,
  kind: "opportunities" | "products" = "opportunities",
) {
  const body = new FormData();
  body.append("file", file);
  const headers = await defaultFetchHeaders();
  delete (headers as Record<string, string>)["Content-Type"];
  return fetchJson<AdministrationMarketplaceOpportunityMedia>(
    `${backend}/administration/marketplace/${kind}/${encodeURIComponent(opportunityId)}/media`,
    { method: "POST", credentials: "include", headers, body },
    "Failed to upload Marketplace opportunity photo",
  ).then(normalizeAdministrationMarketplaceMedia);
}

export const updateAdministrationMarketplaceOpportunityMedia = (
  opportunityId: string,
  mediaId: string,
  change: Partial<Pick<AdministrationMarketplaceOpportunityMedia, "altText" | "representation" | "sortOrder" | "isCover" | "approved">>,
  kind: "opportunities" | "products" = "opportunities",
) => mutate<AdministrationMarketplaceOpportunityMedia>(
  `/administration/marketplace/${kind}/${encodeURIComponent(opportunityId)}/media/${encodeURIComponent(mediaId)}`,
  "PATCH", change, "Failed to update Marketplace opportunity photo",
).then(normalizeAdministrationMarketplaceMedia);

export const updateAdministrationMarketplaceOpportunityGallery = (
  opportunityId: string,
  items: AdministrationMarketplaceOpportunityGalleryItem[],
  kind: "opportunities" | "products" = "opportunities",
) => mutate<AdministrationMarketplaceOpportunityMedia[]>(
  `/administration/marketplace/${kind}/${encodeURIComponent(opportunityId)}/media`,
  "PUT",
  { items },
  "Failed to update Marketplace opportunity gallery",
).then((media) => media.map(normalizeAdministrationMarketplaceMedia));

export async function removeAdministrationMarketplaceOpportunityMedia(
  opportunityId: string,
  mediaId: string,
) {
  return fetchJson<void>(
    `${backend}/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/media/${encodeURIComponent(mediaId)}`,
    { method: "DELETE", credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to remove Marketplace opportunity photo",
  );
}

export const updateAdministrationMarketplaceOpportunityDescription = (
  opportunityId: string,
  change: AdministrationMarketplaceOpportunityDescriptionUpdate,
) => mutate<AdministrationMarketplaceOpportunity>(
  `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}`,
  "PATCH",
  change,
  "Failed to update Marketplace opportunity description",
);

export const updateAdministrationMarketplaceOpportunityAdministrationNotes = (
  opportunityId: string,
  administrationNotes: string,
) => mutate<AdministrationMarketplaceOpportunity>(
  `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/administration-notes`,
  "PATCH",
  { administrationNotes },
  "Failed to update Marketplace opportunity administration notes",
);

export const updateAdministrationMarketplaceOpportunityReviewFields = (
  opportunityId: string,
  change: { title?: string; area?: string },
) => mutate<AdministrationMarketplaceOpportunity>(
  `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/review-fields`,
  "PATCH", change, "Failed to update Marketplace opportunity review fields",
);

export const verifyAdministrationMarketplaceOpportunity = (
  opportunityId: string,
  verification: { method: "provider" | "email" | "phone" | "inbound" | "human"; evidence: string },
) => mutate<AdministrationMarketplaceOpportunity>(
  `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/verify`,
  "POST", verification, "Failed to verify Marketplace opportunity",
);

export const reassessAdministrationMarketplaceOpportunity = (opportunityId: string) =>
  mutate<AdministrationMarketplaceOpportunity>(
    `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/reassess`,
    "POST", {}, "Failed to reassess Marketplace opportunity",
  );

export const publishAdministrationMarketplaceOpportunity = (
  opportunityId: string,
  configuration: { title: string; area: string; description: string; categoryIds: string[] },
) => mutate<AdministrationMarketplaceOpportunity>(
  `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/publish`,
  "POST", configuration, "Failed to publish Marketplace opportunity",
);

export const withdrawAdministrationMarketplaceOpportunity = (opportunityId: string, reason: string) =>
  mutate<AdministrationMarketplaceOpportunity>(
    `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/withdraw`,
    "POST", { reason }, "Failed to withdraw Marketplace opportunity",
  );

export const fetchAdministrationMarketplaceLeadClaims = (
  query: AdministrationMarketplaceLeadClaimQuery = {},
) => {
  const params = new URLSearchParams({
    limit: String(query.limit || 50),
    sort: query.sort || "newest",
  });
  if (query.query?.trim()) params.set("q", query.query.trim());
  if (query.statuses?.length) params.set("status", query.statuses.join(","));
  if (query.reason) params.set("reason", query.reason);
  if (query.remedy !== undefined && query.remedy !== null) params.set("remedy", query.remedy);
  if (query.submittedAfter) params.set("submitted_after", query.submittedAfter);
  if (query.submittedBefore) params.set("submitted_before", query.submittedBefore);
  if (query.reviewedAfter) params.set("reviewed_after", query.reviewedAfter);
  if (query.reviewedBefore) params.set("reviewed_before", query.reviewedBefore);
  if (query.abuseFlagged !== undefined) params.set("abuse_flagged", String(query.abuseFlagged));
  if (query.cursor) params.set("cursor", query.cursor);
  return get<AdministrationMarketplaceLeadClaimPage>(
    `/administration/marketplace/lead-claims?${params.toString()}`,
    "Failed to load Marketplace claims",
  );
};

export const fetchAdministrationMarketplaceLeadClaim = (claimId: string) =>
  get<AdministrationMarketplaceLeadClaimReview>(
    `/administration/marketplace/lead-claims/${encodeURIComponent(claimId)}`,
    "Failed to load Marketplace claim",
  );

export const fetchAdministrationMarketplaceReplacementCandidates = (
  claimId: string,
  query = "",
  includeOtherTiers = false,
) => {
  const params = new URLSearchParams({ limit: "50" });
  if (includeOtherTiers) params.set("include_other_tiers", "true");
  if (query.trim()) params.set("query", query.trim());
  return get<AdministrationMarketplaceReplacementCandidatePage>(
    `/administration/marketplace/lead-claims/${encodeURIComponent(claimId)}/replacement-candidates?${params.toString()}`,
    "Failed to load replacement opportunities",
  );
};

export const decideAdministrationMarketplaceLeadClaim = (
  claimId: string,
  decision: { approved: boolean; reviewerNote: string; remedy?: string; replacementOpportunityId?: string; creditAmountCents?: number; allowanceExtendUntil?: string; correctedInformation?: Record<string, string> },
) => mutate<AdministrationMarketplaceLeadClaim>(
  `/administration/marketplace/lead-claims/${encodeURIComponent(claimId)}/decision`,
  "POST", decision, "Failed to resolve Marketplace claim",
);

export const fetchAdministrationMarketplaceProductActivity = (
  query: AdministrationMarketplaceActivityQuery,
) => {
  const params = new URLSearchParams({
    limit: String(query.limit || 50),
    sort: query.sort || "newest",
  });
  if (query.query?.trim()) params.set("q", query.query.trim());
  if (query.groups?.length) params.set("groups", [...query.groups].sort().join(","));
  if (query.sources?.length) params.set("sources", [...query.sources].sort().join(","));
  if (query.severities?.length) params.set("severities", [...query.severities].sort().join(","));
  if (query.dateFrom) params.set("date_from", query.dateFrom);
  if (query.dateTo) params.set("date_to", query.dateTo);
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.opportunityId) params.set("opportunity_id", query.opportunityId);
  const path = query.productId
    ? `/administration/marketplace/products/${encodeURIComponent(query.productId)}/activity`
    : "/administration/marketplace/activity";
  return get<AdministrationMarketplaceActivityPage>(
    `${path}?${params.toString()}`,
    "Failed to load Marketplace product activity",
  );
};

export const fetchAdministrationDiscoveryActivity = (scope: {
  searchId?: string; runId?: string; source?: string; cursor?: string;
  categoryId?: string; query?: string; offset?: number;
  sort?: string; direction?: "ascending" | "descending";
}, signal?: AbortSignal) => {
  const params = new URLSearchParams({ limit: "50" });
  if (scope.searchId) params.set("search_id", scope.searchId);
  if (scope.runId) params.set("run_id", scope.runId);
  if (scope.source) params.set("source", scope.source);
  if (scope.categoryId) params.set("category_id", scope.categoryId);
  if (scope.query) params.set("q", scope.query);
  if (scope.offset) params.set("offset", String(scope.offset));
  if (scope.cursor) params.set("cursor", scope.cursor);
  if (scope.sort) params.set("sort", scope.sort);
  if (scope.direction) params.set("direction", scope.direction);
  return get<AdministrationAuditPage>(
    `/administration/prospecting/activity?${params.toString()}`,
    "Failed to load Discovery activity",
    signal,
  );
};

export const fetchAdministrationMarketplaceDiscoveryDemandActivity = (demandId: string, sort = "occurred", direction = "descending") =>
  get<AdministrationAuditPage>(
    `/administration/marketplace/discovery-demands/${encodeURIComponent(demandId)}/activity?${new URLSearchParams({ limit: "50", sort, direction })}`,
    "Failed to load Discovery demand activity",
  );

export const fetchAdministrationMarketplaceDiscoveryDemandSignals = (demandId: string, cursor?: string) => {
  const params = new URLSearchParams({ limit: "50" });
  if (cursor) params.set("cursor", cursor);
  return get<AdministrationMarketplaceDiscoveryDemandSignalPage>(
    `/administration/marketplace/discovery-demands/${encodeURIComponent(demandId)}/signals?${params}`,
    "Failed to load Discovery demand signals",
  );
};

export const fetchAdministrationMarketplaceInventoryPolicyActivity = (policyId: string, sort = "occurred", direction = "descending") =>
  get<AdministrationAuditPage>(
    `/administration/marketplace/inventory-policies/${encodeURIComponent(policyId)}/activity?${new URLSearchParams({ limit: "50", sort, direction })}`,
    "Failed to load inventory policy activity",
  );

export const reviewAdministrationMarketplaceOpportunityTier = (opportunityId: string, input: { tier: string; reason: string }) => mutate<AdministrationMarketplaceOpportunity>(`/administration/marketplace/opportunities/${opportunityId}/tier-review`, "POST", input, "Unable to review Marketplace tier");

export type MarketplaceAppointmentConfirmation = {
  contactId: string;
  confirmedAt: string;
  channel: "phone" | "email" | "in_person" | "video";
  requestedAction: "callback" | "meeting" | "walkthrough" | "proposal";
  evidence: string;
};

export const qualifyAdministrationMarketplaceOpportunity = (opportunityId: string, input: MarketplaceAppointmentConfirmation) => mutate<AdministrationMarketplaceOpportunity>(`/administration/marketplace/opportunities/${opportunityId}/appointment-qualification`, "POST", input, "Unable to record venue confirmation");

export const withdrawAdministrationMarketplaceConfirmation = (opportunityId: string, reason: string) => mutate<AdministrationMarketplaceOpportunity>(`/administration/marketplace/opportunities/${opportunityId}/appointment-qualification/withdraw`, "POST", { reason }, "Unable to record withdrawn interest");

export type QualificationConfiguration = {
  positiveVenueTypes?: string[];
  negativeVenueTypes?: string[];
  recentVerificationDays?: number;
  suitability?: { base?: number; venueType?: number; multiSource?: number; completeAddress?: number };
  confidence?: { coordinates?: number; completeAddress?: number; multiSource?: number; recentVerification?: number };
  salesReadiness?: { contact?: number; verifiedContact?: number };
  premiumThresholds?: { suitability?: number; confidence?: number; salesReadiness?: number };
};
export type AdministrationQualificationRuleInput = {
  categoryId: string; suitabilityThreshold: number; confidenceThreshold: number;
  configuration: QualificationConfiguration; enabled: boolean;
  activeFrom: string; activeUntil: string | null;
};
export type AdministrationQualificationRule = AdministrationQualificationRuleInput & {
  id: string; categoryCode: string; categoryName: string; version: number; createdAt: string;
};
export type AdministrationQualificationCategory = { id: string; code: string; name: string };
export const createAdministrationQualificationCategory = (input: { name: string; code: string }) =>
  mutate<AdministrationQualificationCategory>("/administration/prospecting/qualification-categories", "POST", input, "Unable to create vending category");
export const createAdministrationQualificationRule = (input: AdministrationQualificationRuleInput) =>
  mutate<AdministrationQualificationRule>("/administration/prospecting/qualification-rules", "POST", input, "Unable to save qualification rule");
export type DiscoveryAddressResult = { label: string; latitude: number; longitude: number };
export const searchDiscoveryAddress = (query: string, countryCode: string) =>
  mutate<DiscoveryAddressResult[]>("/administration/prospecting/address-search", "POST", { query, countryCode }, "Unable to find address");

export type DiscoveryProgramKind = "sales" | "marketplace";
export type DiscoveryProgramInput = {
  autoPublish?: boolean; continuousGrowth?: boolean; includePlacementTypes?: boolean;
  enabled: boolean; scope: "nationwide" | "selected" | "policy";
  states: string[]; excludedStates: string[]; priorityStates: string[];
  startLatitude: number | null; startLongitude: number | null;
  radiusMeters: number; intervalSeconds: number; searchesPerCycle: number;
  revisitSeconds: number; sourceCodes: string[]; maxResultsPerSource: number; salesEnrichmentLimit: number;
};
export type DiscoveryProgram = {
  id: string | null; configuration: DiscoveryProgramInput; enabled: boolean;
  nextRunAt: string | null; lastError: string; plannedSearches: number; plannedAreas: number;
  generatedSearches: number; remainingSearches: number; runningSearches: number; failedRuns: number;
  searchedAreas?: number; searchedStates?: number; plannedStates?: number; partialRuns?: number;
  createdVenues?: number; observedListings?: number; refreshedRecords?: number;
  queries: string[]; catalogVersion: string;
  nextAreas: Array<{ key: string; name: string; query: string; latitude: number; longitude: number; radiusMeters: number }>;
};
export const discoveryProgramPath = (kind: DiscoveryProgramKind, ownerId: string) => `/administration/prospecting/programs/${kind}/${encodeURIComponent(ownerId)}`;
export const saveDiscoveryProgram = (kind: DiscoveryProgramKind, ownerId: string, input: DiscoveryProgramInput) => mutate<DiscoveryProgram>(discoveryProgramPath(kind, ownerId), "PUT", input, "Unable to save discovery automation");
export type AdministrationMarketplacePublicationBatch = {
  published: number; processed: number; blocked: Array<{ id: string; reason: string }>; nextCursor: string | null;
};
export const publishAdministrationMarketplaceBatch = (cursor?: string | null): Promise<AdministrationMarketplacePublicationBatch> => {
  const params = new URLSearchParams({ limit: "50" });
  if (cursor) params.set("after_id", cursor);
  return mutate<AdministrationMarketplacePublicationBatch>(`/administration/marketplace/opportunities/publication/batch?${params}`, "POST", {}, "Unable to publish Marketplace opportunities");
};
export const fetchDiscoveryProgramHistory = (kind: DiscoveryProgramKind, ownerId: string, sort: string, direction: string, offset: number, query: string) => get<AdministrationAuditPage>(`${discoveryProgramPath(kind, ownerId)}/history?${new URLSearchParams({ sort, direction, offset: String(offset), query })}`, "Unable to load automation activity");

export const updateAdministrationMarketplaceBusinessInformation = (
  opportunityId: string,
  change: { phone: string; website: string; expectedUpdatedAt?: string },
) => mutate<AdministrationMarketplaceOpportunityDetail>(
  `/administration/marketplace/opportunities/${encodeURIComponent(opportunityId)}/business-information`,
  "PATCH", change, "Failed to update business contact information",
);
